import { useCallback, useEffect, useRef, useState } from 'react';
import { AssessPage } from './pages/AssessPage';
import { CasesPage } from './pages/CasesPage';
import { PolicyInputsPage } from './pages/PolicyInputsPage';
import { SettingsPage } from './pages/SettingsPage';
import { UsersPage } from './pages/UsersPage';
import { AccountPage } from './pages/AccountPage';
import { hasPlanningRoles, fetchTermsAcceptedVersion, syncAdministratorFlag } from './api/account';
import { TermsGate } from './TermsGate';
import { TERMS_VERSION } from './terms';
import { LegalPage } from './pages/LegalPage';
import { CONFIG } from './config';
import { login, handleCallback, getAccessToken, refreshAccessToken, getTokenExpiresAt, clearTokens, logout, getUserGroups, getUserSub } from './auth';
import { initWs } from './ws';
import { useToasts, Toasts } from './Toasts';
import { registerGlobalToast } from './globalToast';
import { PwaProvider, usePwa, isStandalone, showIosInstallHint, IosInstallSteps } from './pwa';
import { PlanningPermissionsProvider } from './usePlanningPermissions';
import { isAppLockOn, LOCK_AFTER_MS } from './appLock';
import { LockScreen } from './LockScreen';

const INACTIVITY_MS = 30 * 60 * 1000;
const WARNING_MS    =       60 * 1000;

function getRoute(): string {
  return window.location.hash.replace(/^#\/?/, '') || 'assess';
}

type AuthState = 'loading' | 'login' | 'no-access' | 'terms' | 'app';

function LoginScreen() {
  const { installPrompt, handleInstall } = usePwa();
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg, #f8f9fc)',
    }}>
      <div style={{
        background: 'var(--surface, #fff)', border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 12, padding: 40, width: 'min(380px, 90vw)', textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      }}>
        <img src="./assets/elw-logo.png" alt="ELW" style={{ height: 44, marginBottom: 20 }} />
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: 'var(--ink-1, #111)' }}>
          Elevations Planning
        </h2>
        <p style={{ fontSize: 13, color: 'var(--ink-3, #6b7280)', marginBottom: 28, lineHeight: 1.6 }}>
          Sign in to your account to continue.
        </p>
        <button
          style={{
            background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: 8,
            padding: '11px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%',
          }}
          onClick={() => void login(CONFIG.cognitoDomain, CONFIG.clientId)}
        >
          Sign in with Cognito
        </button>
        {!isStandalone && (
          <>
            <button
              style={{
                background: 'transparent', color: '#1e3a8a', border: '1px solid #1e3a8a', borderRadius: 8,
                padding: '11px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 10,
              }}
              onClick={() => { if (installPrompt) void handleInstall(); else setShowInstallHelp(o => !o); }}
            >
              Install on this device
            </button>
            {showInstallHelp && !installPrompt && (
              <div style={{ marginTop: 14, textAlign: 'left', fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2, #374151)' }}>
                {showIosInstallHint ? (
                  <IosInstallSteps />
                ) : (
                  <>Open your browser&apos;s menu and choose <strong>Install app</strong> or <strong>Add to Home screen</strong>. If you&apos;ve already installed it, open it from your home screen.</>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function resolvePageElement(page: string, subId: string) {
  switch (page) {
    case 'assess':        return <AssessPage id={subId || undefined} />;
    case 'cases':         return <CasesPage id={subId || undefined} />;
    case 'policy-inputs': return <PolicyInputsPage id={subId || undefined} />;
    case 'settings':      return <SettingsPage />;
    case 'users':         return <UsersPage />;
    case 'account':       return <AccountPage />;
    case 'legal':         return <LegalPage />;
    default:             return <AssessPage />;
  }
}

export default function App() {
  return <PwaProvider><AppContent /></PwaProvider>;
}

function AppContent() {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [route, setRoute] = useState(getRoute);
  const { toasts: globalToasts, addToast: globalAddToast, removeToast: globalRemoveToast } = useToasts();
  useEffect(() => { registerGlobalToast(globalAddToast, globalRemoveToast); }, [globalAddToast, globalRemoveToast]);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const resetInactivityRef = useRef<() => void>(() => { /* initialised in effect */ });
  // Only ever true in an installed app whose owner turned the lock on (Account page), on this device.
  const [locked, setLocked] = useState(() => isAppLockOn(getUserSub()));

  useEffect(() => {
    async function init() {
      if (window.location.search.includes('code=')) {
        try {
          // The page reloads to drop the code from the address; stop here so this soon-to-go page doesn't also open a
          // live connection. On an iPhone the two connections could cross, and a result went to the wrong one.
          if (await handleCallback(CONFIG.cognitoDomain, CONFIG.clientId)) return;
        } catch (e) {
          console.error('Auth callback failed:', e);
          setAuthState('login');
          return;
        }
      }
      // An installed app keeps you signed in: a launch after the access token has lapsed
      // (they last about an hour) quietly trades the refresh token for a new one.
      if (isStandalone && !getAccessToken()) {
        await refreshAccessToken(CONFIG.cognitoDomain, CONFIG.clientId);
      }
      if (getAccessToken()) {
        // Someone from another ELW application (e.g. Forecourt) isn't in Planning's group. A closed Planning account
        // still is, but has no Planning roles left; it is turned away exactly the same, as if it had never existed.
        // If the roles can't be read, let them in rather than lock everyone out.
        const sub = getUserSub();
        if (!getUserGroups().includes(CONFIG.requiredGroup) || (sub && await hasPlanningRoles(sub).catch(() => true) === false)) {
          clearTokens();
          setAuthState('no-access');
          return;
        }
        if (CONFIG.wsUrl) {
          initWs(CONFIG.wsUrl, getAccessToken);
        }
        // Everyone accepts the current Terms before going further. If the accepted version can't be read, ask again:
        // accepting twice does no harm.
        const accepted = sub ? await fetchTermsAcceptedVersion(sub).catch(() => null) : null;
        // Checked again here: on a launch that had to refresh the session, who's signed in wasn't known earlier.
        if (isAppLockOn(sub)) setLocked(true);
        setAuthState(accepted === TERMS_VERSION ? 'app' : 'terms');
        if (sub) void syncAdministratorFlag(sub).catch(() => undefined);
      } else {
        setAuthState('login');
      }
    }
    void init();
  }, []);

  useEffect(() => {
    const handler = () => setRoute(getRoute());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  // Proactively refresh the access token 5 minutes before it expires.
  useEffect(() => {
    if (authState !== 'app') return;
    const id = setInterval(async () => {
      const expiresAt = getTokenExpiresAt();
      if (expiresAt === null) { setAuthState('login'); return; }
      if (expiresAt - Date.now() < 5 * 60 * 1000) {
        const result = await refreshAccessToken(CONFIG.cognitoDomain, CONFIG.clientId);
        if (result === 'expired') { clearTokens(); setAuthState('login'); }
      }
    }, 60_000);
    // A backgrounded phone app has its timers frozen (and a background tab has them slowed), so refresh as soon as it is
    // brought back. Requests also renew a lapsed token themselves (auth.ts ensureAccessToken); this keeps the live
    // connection's token fresh too.
    async function onVisible() {
      if (document.visibilityState !== 'visible') return;
      const expiresAt = getTokenExpiresAt();
      if (expiresAt === null || expiresAt - Date.now() >= 5 * 60 * 1000) return;
      const result = await refreshAccessToken(CONFIG.cognitoDomain, CONFIG.clientId);
      if (result === 'expired') { clearTokens(); setAuthState('login'); }
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [authState]);

  // Lock again when the app comes back after more than LOCK_AFTER_MS in the background.
  useEffect(() => {
    if (authState !== 'app') return;
    let hiddenAt: number | null = null;
    function onChange() {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt !== null && Date.now() - hiddenAt > LOCK_AFTER_MS && isAppLockOn(getUserSub())) setLocked(true);
      hiddenAt = null;
    }
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [authState]);
  const handleUnlocked = useCallback(() => setLocked(false), []);

  // Log out after INACTIVITY_MS of idle time, with a warning beforehand. Not in an installed
  // app, which stays signed in until the refresh token itself expires.
  const handleStaySignedIn = useCallback(() => resetInactivityRef.current(), []);
  useEffect(() => {
    if (authState !== 'app' || isStandalone) return;
    let logoutTimer: ReturnType<typeof setTimeout>;
    let warnTimer: ReturnType<typeof setTimeout>;
    function reset() {
      clearTimeout(logoutTimer);
      clearTimeout(warnTimer);
      setShowInactivityWarning(false);
      warnTimer   = setTimeout(() => setShowInactivityWarning(true), INACTIVITY_MS - WARNING_MS);
      logoutTimer = setTimeout(() => logout(CONFIG.cognitoDomain, CONFIG.clientId), INACTIVITY_MS);
    }
    resetInactivityRef.current = reset;
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;
    events.forEach(e => document.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(logoutTimer);
      clearTimeout(warnTimer);
      events.forEach(e => document.removeEventListener(e, reset));
    };
  }, [authState]);

  if (authState === 'loading') {
    return (
      // The same shimmering icon index.html shows before the code loads (its styles live there), so nothing jumps.
      <div className="boot" role="status" aria-label="Loading">
        <div className="boot-icon"><img src="./assets/loading-icon.png" alt="" /></div>
      </div>
    );
  }

  if (authState === 'no-access') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg, #f8f9fc)',
      }}>
        <div style={{
          background: 'var(--surface, #fff)', border: '1px solid var(--border, #e5e7eb)',
          borderRadius: 12, padding: 40, width: 'min(380px, 90vw)', textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <img src="./assets/elw-logo.png" alt="ELW" style={{ height: 44, marginBottom: 20 }} />
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: 'var(--ink-1, #111)' }}>
            Access denied
          </h2>
          <p style={{ fontSize: 13, color: 'var(--ink-3, #6b7280)', marginBottom: 28, lineHeight: 1.6 }}>
            Your account does not have access to this application. Please contact your administrator.
          </p>
          <button
            style={{
              background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: 8,
              padding: '11px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%',
            }}
            // Ends the Cognito session too, so the next sign-in asks for the password again.
            onClick={() => logout(CONFIG.cognitoDomain, CONFIG.clientId)}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (authState === 'login') {
    return <LoginScreen />;
  }

  if (authState === 'terms') {
    return <><Toasts items={globalToasts} onRemove={globalRemoveToast} /><TermsGate onAccepted={() => setAuthState('app')} /></>;
  }

  const [page, subId] = route.split('?')[0].split('/');

  return (
    <PlanningPermissionsProvider>
      {locked && <LockScreen onUnlocked={handleUnlocked} onSignOut={() => logout(CONFIG.cognitoDomain, CONFIG.clientId)} />}
      <Toasts items={globalToasts} onRemove={globalRemoveToast} />
      {showInactivityWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#b45309', color: '#fff', padding: '10px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14,
        }}>
          <span>You've been inactive for a while. You'll be signed out in 1 minute.</span>
          <button
            onClick={handleStaySignedIn}
            style={{
              background: '#fff', color: '#b45309', border: 'none', borderRadius: 6,
              padding: '4px 14px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Stay signed in
          </button>
        </div>
      )}
      {resolvePageElement(page, subId)}
    </PlanningPermissionsProvider>
  );
}
